import type { StorefrontBranding, StorefrontItem } from "@lucralink/shared";
import { DEFAULT_STOREFRONT_ACCENT, DEFAULT_STOREFRONT_LAYOUT } from "@lucralink/shared";
import { formatPriceCents } from "@/lib/marketplace";

interface StorefrontPreviewProps {
  branding: StorefrontBranding;
  items: readonly StorefrontItem[];
}

/**
 * Preview client-side aproximado do que o Worker SSR renderiza. Nao chama a
 * rede: usa o branding e os itens ja carregados no painel para dar feedback
 * imediato da vitrine. So itens visiveis aparecem, espelhando o fail-closed
 * da RPC publica.
 */
export function StorefrontPreview({ branding, items }: StorefrontPreviewProps): JSX.Element {
  const accent = branding.accentColor ?? DEFAULT_STOREFRONT_ACCENT;
  const layout = branding.layout ?? DEFAULT_STOREFRONT_LAYOUT;
  const storeName = branding.storeName?.trim() || "Sua vitrine";
  const visibleItems = items.filter((item) => item.isVisible);

  return (
    <div
      className="overflow-hidden rounded-xl border border-border bg-background"
      style={{ ["--vitrine-accent" as string]: accent }}
      data-testid="storefront-preview"
    >
      <div className="space-y-2 px-4 pb-3 pt-5 text-center">
        {branding.logoUrl ? (
          <img
            src={branding.logoUrl}
            alt=""
            className="mx-auto h-14 w-14 rounded-full border border-border object-cover"
            width={56}
            height={56}
          />
        ) : (
          <div
            className="mx-auto flex h-14 w-14 items-center justify-center rounded-full text-base font-bold text-white"
            style={{ backgroundColor: accent }}
            aria-hidden="true"
          >
            {storeName.slice(0, 1).toUpperCase()}
          </div>
        )}
        <h2 className="text-lg font-semibold text-foreground">{storeName}</h2>
        {branding.bio?.trim() && (
          <p className="mx-auto max-w-xs text-sm text-muted-foreground">{branding.bio}</p>
        )}
      </div>

      {visibleItems.length === 0 ? (
        <p className="px-4 pb-6 text-center text-sm text-muted-foreground">
          Adicione itens para ver a vitrine ganhar forma.
        </p>
      ) : layout === "grid" ? (
        <div className="grid grid-cols-2 gap-2 px-3 pb-4">
          {visibleItems.map((item) => (
            <PreviewCard key={item.id} item={item} accent={accent} />
          ))}
        </div>
      ) : (
        <div className="space-y-2 px-3 pb-4">
          {visibleItems.map((item) => (
            <PreviewButton key={item.id} item={item} accent={accent} />
          ))}
        </div>
      )}
    </div>
  );
}

function PreviewCard({ item, accent }: { item: StorefrontItem; accent: string }): JSX.Element {
  const price = formatPriceCents(item.priceCents);
  return (
    <div className="overflow-hidden rounded-lg border border-border bg-[hsl(var(--surface)/0.75)]">
      <div className="aspect-square bg-background/60">
        {item.imageUrl && (
          <img src={item.imageUrl} alt="" className="h-full w-full object-cover" loading="lazy" />
        )}
      </div>
      <div className="space-y-1 p-2">
        <p className="line-clamp-2 text-xs font-medium text-foreground">{item.title}</p>
        {price && (
          <p className="text-sm font-semibold" style={{ color: accent }}>
            {price}
          </p>
        )}
      </div>
    </div>
  );
}

function PreviewButton({ item, accent }: { item: StorefrontItem; accent: string }): JSX.Element {
  const price = formatPriceCents(item.priceCents);
  return (
    <div
      className="flex items-center justify-between gap-2 rounded-lg border px-3 py-2.5"
      style={{ borderColor: accent }}
    >
      <span className="truncate text-sm font-medium text-foreground">{item.title}</span>
      {price && (
        <span className="shrink-0 text-sm font-semibold" style={{ color: accent }}>
          {price}
        </span>
      )}
    </div>
  );
}
